<template>
<!--Selected Instrument Details page-->
<div class="map-detail-instruments-list">
  <b-container fluid>
    <b-row>
      <b-col cols="12">
        <h5>
          <!--Inst icons in separate component-->
          <inst-icons :instAT="inst.asset_type"></inst-icons>
          {{inst.instrument_name}} Details
          <!--Blue cloud to indicate: cloud data is available-->
          <i
            :id="`${inst.reference_designator}--cloud-info-available`"
            v-if="inst.harvest_flag === true"
            class="fas fa-cloud fa-primary"
          >
            <b-popover
              :target="`${inst.reference_designator}--cloud-info-available`"
              triggers="hover"
              placement="bottom"
            >
              Cloud data available.
            </b-popover>
          </i>
          <!--Grey cloud to indicate: no cloud data available-->
          <i
            :id="`${inst.reference_designator}--cloud-info-unavail`"
            v-if="inst.harvest_flag === false"
            class="fas fa-cloud text-muted"
          >
            <b-popover
              :target="`${inst.reference_designator}--cloud-info-unavail`"
              triggers="hover"
              placement="bottom"
              no-fade
            >
              Currently not in Interactive Oceans systems. <br>
              <a
                :href="`https://ooinet.oceanobservatories.org/data_access/?search=${inst.reference_designator}`"
                target="_blank"
              >Click here for official OOI System.</a>
            </b-popover>
          </i>
          <b-badge
            id="discontinued-badge"
            v-if="inst.discontinued"
            pill
            variant="secondary"
          >Discontinued</b-badge>
        </h5>
        <!-- Row of descriptions-->
        <b-row>
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Description: </strong>
              {{inst.description}}
            </div>
          </b-col>
          <!--Status indicator text using nereus information-->
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Status: </strong>
              <status-text :instRD="inst.reference_designator"></status-text>
            </div>
          </b-col>
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Reference Designator: </strong>
              {{inst.reference_designator}}
            </div>
          </b-col>
          <b-col cols="6">
            <div class="instrument-attribute">
              <strong>Latitude: </strong>
              {{inst.lat}}
            </div>
          </b-col>
          <b-col cols="6">
            <div class="instrument-attribute">
              <strong>Longitude: </strong>
              {{inst.lon}}
            </div>
          </b-col>
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Model: </strong>
              {{inst.model ? inst.model : "N/A"}}
            </div>
          </b-col>
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Manufacturer: </strong>
              {{inst.manufacturer ? inst.manufacturer : "N/A"}}
            </div>
          </b-col>
          <b-col cols="12">
            <div class="instrument-attribute">
              <strong>Owner: </strong>
              {{inst.owner ? inst.owner : "N/A"}}
            </div>
          </b-col>
        </b-row>
        <b-row class="pt-3 pb-5">
          <b-col cols="12">
            <b-tabs content-class="mt-3" fill>
              <b-tab>
                <template v-slot:title>
                  Deployments
                  <b-badge pill variant="info">{{deployments.length}}</b-badge>
                  <b-spinner
                    v-if="loadingDeployments"
                    class="ml-1"
                    type="border"
                    small
                  ></b-spinner>
                </template>
                <p v-if="deployments.length === 0" class="text-center">
                  <strong>No deployments found.</strong>
                </p>
                <b-table
                  v-if="deployments.length > 0"
                  hover
                  :items="deployments"
                  small
                  fixed
                  stacked
                >
                </b-table>
              </b-tab>
              <b-tab>
                <template v-slot:title>
                  Annotations
                  <b-badge pill variant="info">{{annotations.length}}</b-badge>
                  <b-spinner
                    v-if="loadingAnnotations"
                    class="ml-1"
                    type="border"
                    small
                  ></b-spinner>
                </template>
                <p v-if="annotations.length === 0" class="text-center">
                  <strong>No annotations found.</strong>
                </p>
                <b-table
                  v-if="annotations.length > 0"
                  hover
                  :items="annotations"
                  small
                  fixed
                  stacked
                >
                </b-table>
              </b-tab>
              <b-tab :disabled="!inst.harvest_flag">
                <template v-slot:title>
                  Data Availability
                </template>
                <show-DA :refdes="inst.reference_designator"></show-DA>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</div>
</template>
<script>
import _ from 'lodash';
import axios from 'axios';
import { DateTime } from 'luxon';
import { mapActions } from 'vuex';
import { APIBASEURL, APIMETADATA } from '@/utils/constants';
import StatusIndicatorTextVue from '../statusIndicators/StatusIndicatorText';
import InstIcons from './instIcons';
import DACardVue from '../DACard';

export default {
  props: ['inst'],
  data() {
    return {
      deployments: [],
      annotations: [],
      loadingDeployments: false,
      loadingAnnotations: false,
    };
  },
  mounted() {
    this.fetchDeployments();
    this.fetchAnnotations();
  },
  components: {
    'status-text': StatusIndicatorTextVue,
    'inst-icons': InstIcons,
    'show-DA': DACardVue,
  },
  watch: {
    /**
     * Whenever new inst data is passed to this component, get deployments
     * and annotations to update the information displayed.
     * @param {*} newVal - new inst object value
     * @param {*} oldVal - old inst object value
     */
    inst(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchDeployments();
        this.fetchAnnotations();
      }
    },
  },
  methods: {
    ...mapActions([
      'toggleSidebar',
    ]),
    /**
     * This updates the @change event on the sidebar which will
     * make the sidebar visible or hidden
     * @param {*} value - boolean
     */
    updateSidebar(value) {
      this.toggleSidebar({ showSidebar: value });
    },
    /** This method calls the deployment endpoint with params: reference
     * designator.
     */
    fetchDeployments() {
      this.loadingDeployments = true;
      const refdes = this.inst.reference_designator;
      const params = {
        refdes,
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_deployments`, { params })
        .then((resp) => {
          this.processDeployments(resp.data);
          this.loadingDeployments = false;
        });
    },
    /** This method processes the date from the deployment end point for a
     * specific reference designator. The table will display the deployments
     * in order of most recent to oldest. The table headings will be (from
     * left to right): deployment number, UID, start date, end date, location
     * (latitude and longitude).
     */
    processDeployments(deployments) {
      const sortedDeployments = _.reverse(
        _.sortBy(deployments, ['deployment_number']),
      );
      this.deployments = sortedDeployments.map((dep) => {
        const newDep = {
          number: dep.deployment_number,
          UID: dep.uid,
          start_date: DateTime.fromMillis(dep.deployment_start)
            .toUTC()
            .toLocaleString(DateTime.DATETIME_FULL),
          end_date:
            dep.deployment_end !== null
              ? DateTime.fromMillis(dep.deployment_end)
                .toUTC()
                .toLocaleString(DateTime.DATETIME_FULL)
              : 'Ongoing',
          location: `(${dep.lat}, ${dep.lon})`,
        };
        return newDep;
      });
    },
    /** This method calls the annotation end point with params: reference
     * designator, start date and end date.
     */
    fetchAnnotations() {
      this.loadingAnnotations = true;
      const refdes = this.inst.reference_designator;
      const params = {
        ref: refdes,
        start_dt: DateTime.utc(2014, 1, 1, 0, 0, 0).toFormat('yyyyLLdd'),
        end_dt: DateTime.utc().toFormat('yyyyLLdd'),
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_annotations`, { params })
        .then((resp) => {
          this.processAnnotations(resp.data.annotations[refdes]);
          this.loadingAnnotations = false;
        });
    },
    /** This method processes the date from the annotation end point for a
     * specific reference designator. The table will display the annotations
     * in order of most recent to oldest. The annotation table will display the
     * annotation ID, reference designator, start date, end date, comment,
     * author, parameters, QC (quality control) code, and exclusion flag.
     */
    processAnnotations(annotationData) {
      this.annotations = annotationData.map((anno) => {
        const newRefList = [
          anno.site_rd,
          anno.infrastructure_rd,
          anno.instrument_rd,
          anno.stream_method,
          anno.stream_rd,
        ];
        const newAnno = {
          annotation_ID: anno.id,
          reference: _.join(
            _.remove(newRefList, (r) => r !== null),
            '-',
          ),
          start:
            anno.begin_date !== null
              ? DateTime.fromMillis(anno.begin_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          end:
            anno.end_date !== null
              ? DateTime.fromMillis(anno.end_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          comment: anno.annotation,
          author: anno.source,
          parameters:
            anno.parameters.length > 0 ? _.join(anno.parameters, ', ') : 'N/A',
          QC_flag: anno.qc_flag !== null ? _.startCase(anno.qc_flag) : 'N/A',
          exclude: anno.exclusion_flag ? 'Yes' : 'No',
        };
        return newAnno;
      });
    },
  },
};
</script>
<style>
.instrument-attribute {
  background-color: rgba(211, 211, 211, 0.3);
  border-radius: 0.5rem;
  margin: 0.3rem 0 0.3rem 0;
  padding: 0.7rem;
}
#discontinued-badge {
  vertical-align: text-top;
  font-size: 65%;
}
</style>
